import "./js/utils/mainOverrides"
import "ant-design-vue/dist/reset.css"
import "./styles/tailwind.css"
import "./styles/main.scss"
import "flag-icons/css/flag-icons.min.css"
import "vue-virtual-scroller/dist/vue-virtual-scroller.css"
// Ensure Functions service is registered in environments with aggressive tree-shaking
import "firebase/functions"

import { FirebaseTokenLoader, JobsService, PusherWebSocket, SDKObject, SDKObjectListener } from "@telarya/telarya-sdk"
import { message, theme as antdTheme } from "ant-design-vue"
import { initializeApp } from "firebase/app"
import { getAuth } from "firebase/auth"
import { getFirestore } from "firebase/firestore"
import { getFunctions } from "firebase/functions"
import { getStorage } from "firebase/storage"
import { reactive, ref } from "vue"

import { PusherSocket } from "@/components/Other/PusherWebsocketProvider"
import { i18n } from "@/i18n"
import { createBootstrappedApp } from "@/init/appBootstrap"
import { handlePotentialAdBlockSignal, installAdBlockDetection } from "@/init/adBlockDetection"
import {
  markUserInitialized,
  myUser,
  requestTenantSelection,
  requestUserProfileCompletion,
  resetAuthState,
  resolveTenantSelection,
  selectedTenantId,
  setTenantCompanies,
  setTenantMembershipLoadState,
  tenantCompanies,
  tenantCompany,
  tenantSelectionState,
  tenantUser
} from "@/init/authState"
import { getFirebaseConfig } from "@/init/firebaseConfigProvider"
import { installChunkRecovery } from "@/init/chunkRecovery"
import { setSentryUserContext } from "@/init/sentry"
import { storesInitializer } from "@/init/storesInitializer"
import { createAuthenticatedHttpClient } from "@/services/httpClient"
import { initializeStepTelarDocumentRequirementsConfig } from "@/services/StepTelarDocumentRequirementsConfigService"
import { loadTheme } from "@/styles/loadTheme"
import { availableThemes, FALLBACK_THEME, getThemeDefinition } from "@/styles/themeConfig"
import reportError from "@/utils/reportError"

import generateEndpoint from "./endpoint.js"
import { hasPermission } from "./js/Rules"
import router from "./router"

const TENANT_SELECTION_PREFIX = "tenant-selection"
const buildTenantSelectionKey = (firebaseUid) => (firebaseUid ? `${TENANT_SELECTION_PREFIX}:${firebaseUid}` : null)

const loadStoredTenantId = (firebaseUid) => {
  const key = buildTenantSelectionKey(firebaseUid)
  if (!key) return null
  try {
    return localStorage.getItem(key)
  } catch (error) {
    reportError(error, { context: "tenantSelection.load", firebaseUid })
    return null
  }
}

const persistTenantId = (firebaseUid, tenantId) => {
  const key = buildTenantSelectionKey(firebaseUid)
  if (!key) return
  try {
    if (tenantId) {
      localStorage.setItem(key, tenantId)
    } else {
      localStorage.removeItem(key)
    }
  } catch (error) {
    reportError(error, { context: "tenantSelection.persist", firebaseUid, tenantId })
  }
}

const clearTenantId = (firebaseUid) => {
  const key = buildTenantSelectionKey(firebaseUid)
  if (!key) return
  try {
    localStorage.removeItem(key)
  } catch (error) {
    reportError(error, { context: "tenantSelection.clear", firebaseUid })
  }
}

const filterActiveTenants = (tenants = []) => tenants.filter((tenantItem) => !tenantItem?.isDeleted)

const replaceReactive = (target, source) => {
  Object.keys(target).forEach((key) => delete target[key])
  if (source && typeof source === "object") {
    Object.entries(source).forEach(([key, value]) => {
      target[key] = value
    })
  }
}

const applyUserProfile = (profile, source) => {
  replaceReactive(myUser, profile || {})
  replaceReactive(tenantUser, profile || {})
  if (source) {
    myUser.profileSource = source
  }
}

const isBlankString = (value) => String(value ?? "").trim().length === 0

const fetchAuthenticatedUserProfile = async (jobsClient) => {
  if (!jobsClient) return null

  try {
    const profile =
      typeof jobsClient.getAuthenticatedUser === "function"
        ? await jobsClient.getAuthenticatedUser()
        : typeof jobsClient.GetAuthenticatedUser === "function"
        ? await jobsClient.GetAuthenticatedUser()
        : null
    if (profile) {
      applyUserProfile(profile, "authenticated")
      return profile
    }
  } catch (error) {
    reportError(error, { context: "main.getAuthenticatedUser" })
  }

  // Fallback for older backends (legacy endpoint)
  try {
    const legacyProfile = await jobsClient.getMyUser()
    applyUserProfile(legacyProfile, "legacy")
    return legacyProfile
  } catch (error) {
    reportError(error, { context: "main.getAuthenticatedUser.legacy" })
    return null
  }
}

const fetchTenantCompanyDetails = async (jobsClient, tenantId) => {
  if (!jobsClient) return null
  try {
    const company = await jobsClient.getTenantCompany(tenantId)
    replaceReactive(tenantCompany, company || {})
    return company
  } catch (error) {
    reportError(error, { context: "tenantCompany.fetch", tenantId })
    try {
      const fallbackCompany = await jobsClient.getTenantCompany()
      replaceReactive(tenantCompany, fallbackCompany || {})
      return fallbackCompany
    } catch (fallbackError) {
      reportError(fallbackError, { context: "tenantCompany.fetch.fallback", tenantId })
      message.error(i18n.global.t("tenants.loadCompanyError"))
      return null
    }
  }
}

const fetchTenantUserProfile = async (jobsClient, tenantId) => {
  if (!jobsClient) return null
  try {
    const tenantProfile = await jobsClient.getMyTenantUser(tenantId, tenantId)
    applyUserProfile(tenantProfile, "tenant")
    return tenantProfile
  } catch (error) {
    reportError(error, { context: "tenantUser.fetch", tenantId })
    try {
      const legacyProfile = await jobsClient.getMyUser()
      applyUserProfile(legacyProfile, "legacy")
      message.warning(i18n.global.t("tenants.legacyProfileNotice"))
      return legacyProfile
    } catch (legacyError) {
      reportError(legacyError, { context: "tenantUser.fetch.legacy", tenantId })
      message.error(i18n.global.t("tenants.profileLoadError"))
      return null
    }
  }
}

let JobsServiceClient = null
let tenantReinitializationPromise = null

const clearTenantScopedState = () => {
  replaceReactive(tenantCompany, {})
  replaceReactive(tenantUser, {})
}

const initializeTenantScopedResources = async (jobsClient, tenantId, options = { reinitializeStores: true }) => {
  if (!jobsClient || !tenantId) return
  clearTenantScopedState()
  await fetchTenantCompanyDetails(jobsClient, tenantId)
  await fetchTenantUserProfile(jobsClient, tenantId)

  if (tenantCompany?.id) {
    initializeStepTelarDocumentRequirementsConfig(jobsClient, tenantCompany.id)
    if (options.reinitializeStores !== false) {
      storesInitializer(tenantCompany, PusherSocket)
    }
  }

  setSentryUserContext(myUser, tenantCompany)
}

const ensureTenantSelection = async (firebaseUid, activeTenants) => {
  const storedTenantId = loadStoredTenantId(firebaseUid)
  if (storedTenantId && activeTenants.some((tenantItem) => tenantItem.companyId === storedTenantId)) {
    resolveTenantSelection(storedTenantId)
    return storedTenantId
  }
  if (activeTenants.length === 1) {
    const onlyTenantId = activeTenants[0].companyId
    resolveTenantSelection(onlyTenantId)
    return onlyTenantId
  }
  if (activeTenants.length > 1) {
    return await requestTenantSelection("initial")
  }
  return null
}

const switchTenant = async (tenantId, options = { reload: true }) => {
  if (!tenantId || tenantId === selectedTenantId.value) return
  const firebaseUid = getAuth().currentUser?.uid
  persistTenantId(firebaseUid, tenantId)
  resolveTenantSelection(tenantId)
  if (options.reload !== false) {
    window.location.reload()
    return
  }

  if (tenantReinitializationPromise) {
    await tenantReinitializationPromise
  }

  tenantReinitializationPromise = initializeTenantScopedResources(JobsServiceClient, tenantId, {
    reinitializeStores: true
  })

  try {
    await tenantReinitializationPromise
  } finally {
    tenantReinitializationPromise = null
  }
}

installChunkRecovery(router)

const storedTheme = localStorage.getItem("theme")
const initialThemeName = availableThemes.includes(storedTheme) ? storedTheme : FALLBACK_THEME
loadTheme(initialThemeName)
installAdBlockDetection()

const algorithmMap = {
  light: antdTheme.defaultAlgorithm,
  dark: antdTheme.darkAlgorithm,
  compact: antdTheme.compactAlgorithm
}

const currentThemeName = ref(initialThemeName)
const antdThemeConfig = reactive({
  algorithm: antdTheme.defaultAlgorithm,
  token: {},
  components: {}
})

applyAntdTheme(initialThemeName)

function applyAntdTheme(themeName) {
  const { mode, tokens, components } = getThemeDefinition(themeName)
  const algorithm = algorithmMap[mode] ?? antdTheme.defaultAlgorithm
  antdThemeConfig.algorithm = algorithm
  antdThemeConfig.token = { ...tokens }
  antdThemeConfig.components = components ? JSON.parse(JSON.stringify(components)) : {}
}

function switchTheme(themeName) {
  const targetThemeName = availableThemes.includes(themeName) ? themeName : FALLBACK_THEME
  const themeLink = document.getElementById("theme-css")
  if (themeLink) {
    themeLink.href = `${process.env.BASE_URL}css/${targetThemeName}.css?v=${Date.now()}`
  } else {
    loadTheme(targetThemeName)
  }
  localStorage.setItem("theme", targetThemeName)
  currentThemeName.value = targetThemeName
  applyAntdTheme(targetThemeName)
}

let googleMapsPromise = null
let googleMapsApi = null

const provideGoogleMapsServices = (app, googleMaps) => {
  if (!app?.provide || !googleMaps?.maps?.places) return
  const autocompleteService = new googleMaps.maps.places.AutocompleteService()
  const placesService = new googleMaps.maps.places.PlacesService(document.createElement("div"))
  app.provide("autocompleteService", autocompleteService)
  app.provide("placesService", placesService)
}

const ensureGoogleMapsServices = (app) => {
  if (!app?.$gmapApiPromiseLazy) return
  if (googleMapsApi) {
    provideGoogleMapsServices(app, googleMapsApi)
    return
  }
  if (!googleMapsPromise) {
    googleMapsPromise = app
      .$gmapApiPromiseLazy()
      .then((googleMaps) => {
        googleMapsApi = googleMaps
        provideGoogleMapsServices(app, googleMaps)
        return googleMaps
      })
      .catch((error) => {
        handlePotentialAdBlockSignal(error, "googleMaps.load")
        reportError(error, { context: "googleMaps.load" })
        googleMapsPromise = null
        throw error
      })
    return
  }
  googleMapsPromise
    .then((googleMaps) => {
      if (googleMaps) provideGoogleMapsServices(app, googleMaps)
    })
    .catch((error) => {
      reportError(error, { context: "googleMaps.ensureServices" })
    })
}

const firebaseConfig = getFirebaseConfig()

// Initialize Firebase
const firebaseApp = initializeApp(firebaseConfig)
const firebaseFunctions = getFunctions(firebaseApp)
const database = getFirestore(firebaseApp)
const fileStorage = getStorage(firebaseApp)
let vueApp
const tenantContext = {
  tenantUser,
  tenantCompanies,
  selectedTenantId,
  tenantSelectionState,
  resolveTenantSelection,
  requestTenantSelection,
  switchTenant
}

getAuth().onAuthStateChanged(async (user) => {
  // Reset auth state and the initialization promise for each auth change
  resetAuthState()

  const { default: firestoreFunctions } = await import("@/firestore")
  const { useAuthStore } = await import("@/components/Auth/AuthStore")
  // if (process.env.VUE_APP_DEVELOPMENT_MODE?.toLowerCase() === "development") Pusher.logToConsole = true

  if (!vueApp) {
    // Only start the app on firebase check done;
    vueApp = createBootstrappedApp({
      router,
      firebaseApp,
      firebaseFunctions,
      database,
      fileStorage,
      antdThemeConfig,
      currentThemeName,
      switchTheme,
      tenantCompany,
      myUser,
      tenantContext,
      hasPermission,
      firestoreFunctions
    })
    vueApp.mount("#app")
  }

  if (!user) {
    setSentryUserContext(null, null)
    JobsServiceClient = null
  }

  if (user) {
    vueApp.unmount("#app")
    const httpClient = createAuthenticatedHttpClient(new FirebaseTokenLoader(getAuth()), {
      tenantIdProvider: () => selectedTenantId.value
    })
    JobsServiceClient = new JobsService(generateEndpoint("jobs"), httpClient)

    // Only start the app on firebase check done;
    vueApp = createBootstrappedApp({
      router,
      firebaseApp,
      firebaseFunctions,
      database,
      fileStorage,
      antdThemeConfig,
      currentThemeName,
      switchTheme,
      tenantCompany,
      myUser,
      jobsServiceClient: JobsServiceClient,
      tenantContext,
      hasPermission,
      firestoreFunctions
    })
    ensureGoogleMapsServices(vueApp)
    vueApp.mount("#app")

    const authenticatedProfile = await fetchAuthenticatedUserProfile(JobsServiceClient)
    const requiresProfileCompletion =
      !!authenticatedProfile &&
      (isBlankString(authenticatedProfile?.nameFirst) || isBlankString(authenticatedProfile?.nameLast))
    if (requiresProfileCompletion) {
      await requestUserProfileCompletion()
    }

    let memberships = []
    setTenantMembershipLoadState("loading")
    try {
      const membershipsResponse = await JobsServiceClient.getMyTenantCompanies()
      if (!Array.isArray(membershipsResponse)) {
        throw new Error("Unexpected tenant memberships response")
      }
      memberships = membershipsResponse
      setTenantMembershipLoadState("loaded")
    } catch (error) {
      setTenantMembershipLoadState("error")
      reportError(error, { context: "main.getMyTenantCompanies" })
      message.error(i18n.global.t("tenants.loadError"))
    }

    let activeTenants = filterActiveTenants(memberships || [])
    setTenantCompanies(activeTenants)

    let chosenTenantId = await ensureTenantSelection(user.uid, activeTenants)

    if (!chosenTenantId && selectedTenantId.value) {
      chosenTenantId = selectedTenantId.value
    }

    // When the user is not linked to any tenant company yet, we skip tenant-scoped initialization and
    // let router guards redirect them to the invite redemption flow.
    if (!chosenTenantId) {
      // fall through
    } else {
      persistTenantId(user.uid, chosenTenantId)

      tenantReinitializationPromise = initializeTenantScopedResources(JobsServiceClient, chosenTenantId)
      try {
        await tenantReinitializationPromise
      } finally {
        tenantReinitializationPromise = null
      }

      SDKObjectListener.WebSocket = new PusherWebSocket(PusherSocket)
      if (process.env.NODE_ENV !== "production") {
        SDKObjectListener.Logging = (...msg) => console.log("SDKObjectListener", msg)
      }
      SDKObjectListener.JobsServiceClient = JobsServiceClient
      SDKObject.ProxyCreator = (self) => {
        const proxy = reactive(self)
        self.proxyValueUpdater = (key, value) => {
          proxy[key] = value
        }
        return proxy
      }
      SDKObject.SetPropertyInterceptor = (self, key, value) => {
        if (self.proxyValueUpdater != null) self.proxyValueUpdater(key, value)
      }
    }
  }

  // Keep Pinia auth store in sync with Firebase user
  const authStore = useAuthStore()
  authStore.setUserFromFirebase(user)

  // Resolve to indicate setup is complete for this auth state
  markUserInitialized()
})
